'use client';

import { useEffect, useRef, useState } from 'react';

interface CountUpProps {
  /** Final number. SSR/no-JS renders it directly — the animation is a
   *  post-hydration enhancement, mirroring animations.tsx semantics. */
  value: number;
  /** Localized string with a literal __N__ placeholder where the
   *  number sits (locale-safe: number position varies per language). */
  template: string;
}

/**
 * Scroll-triggered number count-up (BAL-662 Phase 7 item 4). Visible
 * final value by default; only elements below the fold at mount get
 * the 0→value tween when scrolled into view (same IO discipline as
 * animations.tsx Reveal). Reduced-motion stays static.
 */
export function CountUp({ value, template }: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const el = ref.current;
    if (!el || value <= 0) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const rect = el.getBoundingClientRect();
    const inView = rect.top < window.innerHeight - 80 && rect.bottom > 0;
    if (inView) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const duration = 900;
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          setDisplay(Math.round(eased * value));
          if (t < 1) requestAnimationFrame(tick);
        };
        setDisplay(0);
        requestAnimationFrame(tick);
      },
      { rootMargin: '0px 0px -80px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value]);

  return <span ref={ref}>{template.replace('__N__', String(display))}</span>;
}
